import dayjs from 'dayjs'
import {
  BreadcrumbsInput,
  DossbotChatRunStatus,
  DossbotChatsDocument,
  DossbotChatsQuery,
  useCreateDossbotChatMutation,
} from '~/common/generated/apollo-operations'
import { usePage } from '~/common/hooks/usePage'

/**
 * Shared hook for creating a new Dossbot chat.
 * Used by both DossbotNewChat and Home.
 *
 * @param includeBreadcrumb - Whether to attach the current page's breadcrumb context to the chat.
 *   Defaults to true. Set to false when calling from pages without meaningful entity context (e.g., home page).
 */
export const useCreateNewDossbotChat = ({ includeBreadcrumb = true } = {}) => {
  const [createDossbotChat] = useCreateDossbotChatMutation()
  const { objectId, objectType } = usePage()

  const createChat = async (prompt: string): Promise<string | null> => {
    const breadcrumb: BreadcrumbsInput | undefined =
      includeBreadcrumb && objectId && objectType ? { objectId, objectType } : undefined
    try {
      const result = await createDossbotChat({
        variables: {
          input: {
            prompt,
            breadcrumb,
          },
        },
        // Ensure data is returned even if there are partial GraphQL errors
        errorPolicy: 'all',
        update: (cache, { data }) => {
          if (!data) {
            return
          }

          const queryData: DossbotChatsQuery | null = cache.readQuery({
            query: DossbotChatsDocument,
          })

          if (queryData) {
            cache.writeQuery({
              query: DossbotChatsDocument,
              data: {
                dossbotChats: [data.createDossbotChat, ...queryData.dossbotChats],
              },
            })
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createDossbotChat: {
            __typename: 'DossbotChat',
            id: 'temp-id',
            title: 'New Chat',
            summary: null,
            runs: [
              {
                __typename: 'DossbotChatRun',
                id: 'temp-id',
                createdAt: dayjs().toISOString(),
                completedAt: null,
                inputPrompt: prompt,
                response: null,
                status: DossbotChatRunStatus.IN_PROGRESS,
              },
            ],
          },
        },
      })

      return result.data?.createDossbotChat.id ?? null
    } catch (error) {
      console.error('Failed to create new chat:', error)
      return null
    }
  }

  return { createChat }
}
